// src/components/ui/StatusDisplay.jsx
import React from 'react';
import { Chip, useTheme, alpha, Avatar } from '@mui/material';
import PropTypes from 'prop-types';
import { 
  getStatusConfig, 
  getRegionConfig, 
  getPriorityConfig 
} from '../../utils/statusConfig';

/**
 * Unified status display component for all entity statuses, regions, priorities, etc.
 * Replaces StatusChip and StatusBadge with a single configurable component
 * 
 * @param {Object} props
 * @param {string} props.type - Type of display ('status', 'region', 'priority', 'custom')
 * @param {string} props.entityType - For status type, what entity ('client', 'brand', 'task', 'project')
 * @param {string} props.value - The value to display (status value, region name, etc.)
 * @param {string} props.size - Size of chip ('small', 'medium')
 * @param {string} props.variant - Variant of chip ('filled', 'outlined')
 * @param {Object} props.customProps - For custom type, direct props to pass to Chip
 */
const StatusDisplay = ({
  type = 'status',
  entityType = 'task',
  value,
  size = 'small',
  variant = 'filled',
  customProps = {},
  ...rest
}) => {
  const theme = useTheme();

  // If no value provided, return null
  if (!value && !customProps.label) return null;

  // For status displays
  if (type === 'status') {
    const config = getStatusConfig(entityType, value);
    
    return (
      <Chip
        label={config.label}
        color={config.color}
        size={size}
        icon={config.icon}
        variant={variant}
        sx={{ fontWeight: 500, ...rest.sx }}
        {...rest}
      />
    );
  }

  // For region displays
  if (type === 'region') {
    const config = getRegionConfig(value);
    
    return (
      <Chip 
        label={value} 
        size={size}
        sx={{ 
          backgroundColor: alpha(config.color, 0.1),
          color: config.color,
          fontWeight: 500,
          '& .MuiChip-avatar': {
            color: 'inherit',
            backgroundColor: 'transparent',
          },
          ...rest.sx
        }}
        avatar={<Avatar>{config.icon}</Avatar>}
        {...rest}
      />
    );
  }

  // For priority displays
  if (type === 'priority') {
    const config = getPriorityConfig(value);
    
    return (
      <Chip
        label={config.label}
        color={config.color}
        size={size}
        variant={variant}
        sx={{ fontWeight: 500, ...rest.sx }}
        {...rest}
      />
    );
  }

  // For custom displays with direct props
  if (type === 'custom') {
    return <Chip size={size} variant={variant} {...customProps} {...rest} />;
  }

  // Default custom styling for filled variants
  const getCustomStyling = () => {
    if (variant === 'filled' && customProps.color) {
      const colorKey = customProps.color || 'default';
      return {
        backgroundColor: alpha(theme.palette[colorKey]?.main || theme.palette.grey[500], 0.1),
        color: theme.palette[colorKey]?.main || theme.palette.text.primary,
        borderColor: 'transparent',
        fontWeight: 500
      };
    }
    return {};
  };

  // Default case - display as a basic chip with label from value
  return (
    <Chip
      label={value}
      size={size}
      variant={variant}
      sx={{
        ...getCustomStyling(),
        ...rest.sx
      }}
      {...customProps}
      {...rest}
    />
  );
};

StatusDisplay.propTypes = {
  type: PropTypes.oneOf(['status', 'region', 'priority', 'custom']),
  entityType: PropTypes.string,
  value: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  variant: PropTypes.oneOf(['filled', 'outlined']),
  customProps: PropTypes.object
};

export default StatusDisplay;